import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Siren, X } from "lucide-react";
import Logo from "./Logo";

export const scrollToSection = (hash) => {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Track Record", href: "#track-record" },
  { label: "Guarantee", href: "#guarantee" },
  { label: "Success Stories", href: "#success-stories" },
  { label: "Leadership", href: "#leadership" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-sand/10 bg-basalt/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button onClick={() => go("#top")} className="text-left" data-testid="nav-logo-button">
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:text-sand"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-ochre transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={() => go("#intake")}
            className="group flex items-center gap-2 rounded-full border border-ochre/50 bg-ochre/10 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-ochre transition-[background-color,color,border-color] duration-300 hover:bg-ochre hover:text-basalt"
            data-testid="nav-emergency-intake-button"
          >
            <Siren size={14} className="transition-transform duration-300 group-hover:rotate-12" />
            Emergency Intake
          </button>
        </nav>

        <button
          className="text-sand lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-sand/10 bg-basalt/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-mist"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#intake")}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-ochre px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-basalt"
                data-testid="nav-mobile-emergency-intake-button"
              >
                <Siren size={14} />
                Emergency Intake
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
